import React, { useEffect, useRef } from 'react';
import './AppDialog.css';

/**
 * Centred, themed dialog — the replacement for window.alert / window.confirm.
 *
 * Those render a browser chrome box pinned to the top of the viewport, in the OS font, with
 * no relation to the page behind it. They also block the JavaScript thread, so nothing can
 * animate or repaint while one is open. For a "user saved" confirmation that is both ugly
 * and disorienting: the message appears nowhere near what the person just clicked.
 *
 * Deliberately not a toast. The existing ToastProvider is right for passive, auto-dismissing
 * notices in the corner; this is for messages the admin must acknowledge — a save result, a
 * refusal, a destructive confirmation — which belong in the middle of the screen and should
 * wait for a click.
 */
export type AppDialogTone = 'success' | 'error' | 'warning' | 'info';

interface AppDialogProps {
  open: boolean;
  title: string;
  /** Newlines are preserved, so existing multi-line alert copy carries over unchanged. */
  message: React.ReactNode;
  tone?: AppDialogTone;
  confirmLabel?: string;
  /** Null renders a single acknowledge button — the alert() shape. */
  cancelLabel?: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const TONE_ICON: Record<AppDialogTone, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const AppDialog: React.FC<AppDialogProps> = ({
  open,
  title,
  message,
  tone = 'info',
  confirmLabel = 'OK',
  cancelLabel = null,
  busy,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Focus the primary action on open, so the dialog is dismissible from the keyboard
  // without tabbing — alert() gave that for free and it should not be lost in the swap.
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  // Escape closes. Bound to the document rather than the card because focus may sit on the
  // backdrop after a click there.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div className="app-dialog__overlay" onClick={() => !busy && onCancel()}>
      <div
        className="app-dialog__card"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={e => e.stopPropagation()}
      >
        <div className={`app-dialog__icon app-dialog__icon--${tone}`} aria-hidden="true">
          {TONE_ICON[tone]}
        </div>
        <h3 className="app-dialog__title">{title}</h3>
        <div className="app-dialog__message">{message}</div>
        <div className="app-dialog__actions">
          {cancelLabel && (
            <button type="button" className="app-dialog__btn app-dialog__btn--ghost" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            className={`app-dialog__btn app-dialog__btn--primary ${tone === 'error' ? 'app-dialog__btn--danger' : ''}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDialog;
